import { useEffect, useRef } from 'react';
import Konva from 'konva';

interface EditableTextOverlayProps {
  textNode: Konva.Text;
  initialText: string;
  onChange: (text: string, metrics?: { width: number; height: number }) => void;
  onClose: () => void;
  maxWidth?: number;
  noWrap?: boolean;
  centerCanvas?: { x: number; y: number };
  allowEmptyCommit?: boolean;
  selectAllOnMount?: boolean;
}

export const EditableTextOverlay: React.FC<EditableTextOverlayProps> = ({ 
  textNode, 
  initialText,
  onChange, 
  onClose,
  maxWidth,
  noWrap,
  centerCanvas,
  allowEmptyCommit = false,
  selectAllOnMount = true,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | HTMLDivElement | null>(null);
  const isInitialized = useRef(false);
  const isClosing = useRef(false);
  const onChangeRef = useRef(onChange);
  const onCloseRef = useRef(onClose);
  const centerXRef = useRef<number>(0);
  const anchorLeftRef = useRef<number>(0);
  const anchorRightRef = useRef<number>(0);
  const baseWidthRef = useRef<number>(0);
  const baseHeightRef = useRef<number>(0);
  const lockTopLeftRef = useRef<boolean>(false);
  const measureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const transformRafRef = useRef<number | null>(null);
  const alignRef = useRef<string>('left');
  
  // Update refs when props change
  onChangeRef.current = onChange;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!textNode || isInitialized.current) return;
    
    const stage = textNode.getStage();
    if (!stage) {
      onClose();
      return;
    }
    
    // Mark as initialized to prevent recreating
    isInitialized.current = true;
    
    // Create editable element
    const useDiv = !!noWrap;
    const textarea: HTMLTextAreaElement | HTMLDivElement = useDiv
      ? document.createElement('div')
      : document.createElement('textarea');
    if (useDiv) (textarea as HTMLDivElement).contentEditable = 'true';
    textarea.setAttribute('data-testid', 'tid.canvas.textEditor');
    textareaRef.current = textarea;
    document.body.appendChild(textarea);

    // Get stage container element
    const stageContainer = stage.container();

    // Set initial value
    if (useDiv) {
      (textarea as HTMLDivElement).innerText = initialText;
    } else {
      (textarea as HTMLTextAreaElement).value = initialText;
    }

    // Style the textarea to match the text node
    textarea.style.position = 'fixed'; // Use fixed positioning relative to viewport
    textarea.style.border = '1px dashed #9DB5FF'; // Subtle dashed border
    textarea.style.padding = '0px';
    textarea.style.margin = '0px';
    textarea.style.overflow = 'hidden';
    textarea.style.background = 'transparent'; // Transparent background
    textarea.style.outline = 'none'; // Remove outline, using border instead
    textarea.style.resize = 'none';
    textarea.style.lineHeight = `${textNode.lineHeight() || 1.2}`;
    textarea.style.fontFamily = textNode.fontFamily() || 'system-ui, -apple-system, sans-serif';
    // Map Konva fontStyle (e.g., 'bold italic') to CSS font-weight + font-style
    const fsRaw = (textNode.fontStyle() || '').toString().toLowerCase();
    const fwRaw = ((textNode as any).fontWeight?.() || '').toString().toLowerCase();
    const isBold = fsRaw.includes('bold') || fwRaw === 'bold' || fwRaw === '700';
    const isItalic = fsRaw.includes('italic');
    textarea.style.fontWeight = isBold ? '700' : '400';
    textarea.style.fontStyle = isItalic ? 'italic' : 'normal';
    const textDecoration = (textNode as any).textDecoration?.() || (textNode as any).getAttr?.('textDecoration') || 'none';
    textarea.style.textDecoration = textDecoration || 'none';
    textarea.style.transformOrigin = 'center center';
    const cssAlign = (((textNode as any).getAttr?.('textAlign') || (textNode.align() as any)) || (noWrap ? 'center' : 'left')) as string;
    alignRef.current = cssAlign;
    textarea.style.textAlign = cssAlign as any;
    textarea.dir = 'ltr';
    textarea.style.direction = 'ltr';
    // Keep last-line alignment consistent with main alignment to avoid visual drift
    (textarea.style as any).setProperty?.('text-align-last', cssAlign);
    (textarea.style as any).setProperty?.('unicode-bidi', 'bidi-override');
    const fillColor = textNode.fill();
    // If fill is transparent or invalid, try to get color from node attrs or use default
    let textColor = typeof fillColor === 'string' ? fillColor : '#333';
    if (textColor === 'transparent' || !textColor || textColor === 'rgba(0,0,0,0)') {
      // Try to get textColor from parent node's attrs (for flowchart shapes)
      try {
        const parent = (textNode as any).getParent?.();
        if (parent) {
          const nodeAttrs = (parent as any).attrs || {};
          const attrsTextColor = nodeAttrs.textColor;
          if (typeof attrsTextColor === 'string' && attrsTextColor && attrsTextColor !== 'transparent') {
            textColor = attrsTextColor;
          } else {
            // Fallback: use a visible default color
            textColor = '#0F172A'; // Default dark color
          }
        } else {
          textColor = '#0F172A'; // Default dark color
        }
      } catch {
        textColor = '#0F172A'; // Default dark color
      }
    }
    textarea.style.color = textColor;
    textarea.style.borderRadius = '2px';
    textarea.style.boxShadow = 'none'; // Remove shadow for cleaner look
    textarea.style.zIndex = '10000';

    // Wrapping/width rules
    // maxWidth is applied during updatePosition/handleInput with current scale
    if (noWrap) {
      if (!useDiv) textarea.setAttribute('wrap', 'off');
      textarea.style.whiteSpace = 'pre';
      textarea.style.overflowX = 'hidden';
      textarea.style.overflowY = 'hidden';
      textarea.style.wordBreak = 'normal';
      textarea.style.overflowWrap = 'normal';
      textarea.style.display = 'inline-block';
    } else {
      textarea.style.whiteSpace = 'pre-wrap';
      textarea.style.overflowWrap = 'anywhere';
    }

    // Track visual center so width/height growth expands about anchor
    const centerYRef = { current: 0 } as { current: number };

    // Setup canvas for precise text measurement per line
    if (!measureCanvasRef.current) {
      measureCanvasRef.current = document.createElement('canvas');
    }
    // Note: canvas text metrics context is configured in updatePosition

    // Height recalculation is defined after initial position (uses center refs)
    
    // Position and size overlay initially using shared function
    const updatePosition = () => {
      const stageBoxNow = stageContainer.getBoundingClientRect();
      const scaleXNow = stage.scaleX();
      const scaleYNow = stage.scaleY();
      const stagePosNow = stage.position();

      // Prefer explicit bounding box from sibling rect for exact alignment
      // Fallback to textNode client rect if bounds not available
      const r = textNode.getClientRect({ skipStroke: true, skipShadow: true });
      let boxW: number | undefined;
      let boxH: number | undefined;
      let boundsRectScreen: { x: number; y: number; width: number; height: number } | undefined;
      try {
        const parent = (textNode as any).getParent?.();
        const bounds = parent?.findOne?.('.text-bounds');
        if (bounds) {
          const br = bounds.getClientRect({ skipStroke: true, skipShadow: true });
          if (typeof br?.width === 'number' && br.width > 0) {
            boundsRectScreen = { x: br.x, y: br.y, width: br.width, height: br.height } as any;
          }
          if (bounds?.width && bounds?.height) {
            const w = bounds.width();
            const h = bounds.height();
            if (typeof w === 'number' && w > 0) boxW = w;
            if (typeof h === 'number' && h > 0) boxH = h;
          }
        }
      } catch {}

      // Compute center anchor in screen pixels (consistent with initial mount)
      if (boundsRectScreen) {
        // Use bounds client rect directly in screen space (relative to stage container)
        const widthPx = Math.max(20, boundsRectScreen.width);
        const heightPx = Math.max(18, boundsRectScreen.height);
        baseWidthRef.current = widthPx;
        baseHeightRef.current = heightPx;
        const leftPx = stageBoxNow.left + boundsRectScreen.x;
        const boundsTopPx = stageBoxNow.top + boundsRectScreen.y;
        
        // Read vertical alignment from textNode to position textarea correctly
        const vAlign = (textNode.verticalAlign?.() as any) || 'top';
        
        // Calculate the text content height to determine vertical offset
        // Estimate based on line count and line height from the initial text
        const lineHeight = textNode.lineHeight() || 1.2;
        const fontSize = textNode.fontSize();
        const estimatedLineHeight = fontSize * lineHeight;
        const currentText = (() => {
          try {
            if (useDiv) return (textarea as HTMLDivElement).innerText || '';
            return (textarea as HTMLTextAreaElement).value || '';
          } catch {
            return '';
          }
        })();
        const textContent = currentText || initialText || '';
        const lineCount = Math.max(1, textContent.split('\n').length);
        const estimatedContentHeight = lineCount * estimatedLineHeight;
        
        // Calculate vertical offset based on alignment
        // This padding-top will push the text content down to match Konva's verticalAlign
        let verticalOffset = 0;
        if (vAlign === 'middle') {
          // Center the content within the bounds
          // Use the actual bounds height for centering
          // If text is empty or very short, center based on line height instead of content height
          if (estimatedContentHeight < estimatedLineHeight * 1.5) {
            verticalOffset = (heightPx - estimatedLineHeight) / 2;
          } else {
            verticalOffset = Math.max(0, (heightPx - estimatedContentHeight) / 2);
          }
        } else if (vAlign === 'bottom') {
          // Align content to bottom of bounds
          verticalOffset = Math.max(0, heightPx - estimatedContentHeight);
        }
        // For 'top', verticalOffset remains 0
        
        // Position textarea to match the text's vertical position
        // The textarea itself is positioned at the top of bounds, but we add padding-top
        // to simulate the vertical alignment offset (matching Konva's behavior)
        textarea.style.left = `${leftPx}px`;
        textarea.style.top = `${boundsTopPx}px`;
        textarea.style.width = `${widthPx}px`;
        textarea.style.height = `${heightPx}px`;
        anchorLeftRef.current = leftPx;
        anchorRightRef.current = leftPx + widthPx;
        textarea.style.paddingTop = `${verticalOffset}px`;
        textarea.style.boxSizing = 'border-box';
        
        // Lock top-left so height changes won't recenter
        // The padding-top handles the vertical alignment offset
        lockTopLeftRef.current = true;
        centerXRef.current = leftPx + widthPx / 2;
        centerYRef.current = boundsTopPx + heightPx / 2;

        // Update font size with scale for visual parity
        const newFontPx = textNode.fontSize() * scaleXNow;
        textarea.style.fontSize = `${newFontPx}px`;
        const ctx2a = measureCanvasRef.current?.getContext('2d');
        if (ctx2a) {
          const fontStyle = `${textNode.fontStyle() || ''}`.trim();
          const fontWeight = `${(textNode as any).fontWeight?.() || ''}`.trim();
          const family = textNode.fontFamily() || 'system-ui, -apple-system, sans-serif';
          ctx2a.font = `${fontStyle ? fontStyle + ' ' : ''}${fontWeight ? fontWeight + ' ' : ''}${newFontPx}px ${family}`;
        }
        const lsRawA = (textNode as any).letterSpacing?.() || 0;
        (textarea.style as any).letterSpacing = `${lsRawA * scaleXNow}px`;
        return; // Positioned using bounds; skip the fallback branch
      }

      // Desired size for fallback path (no explicit bounds node):
      // Konva's getClientRect() returns a rectangle already in the Stage's
      // screen space. Do NOT re-apply stage position/scale to these values.
      // Using them directly avoids double transforms for connector labels.
      const desiredWFromNode = Math.max(20, (boxW ?? r.width));
      const desiredH = Math.max(18, (boxH ?? r.height));

      // If a maxWidth is provided for wrapped editing (text nodes), snap editor width to the box width
      const newMaxWidthPx = typeof maxWidth === 'number' ? Math.max(0, maxWidth) * scaleXNow : undefined;
      const widthPx = (typeof newMaxWidthPx === 'number' && !noWrap) ? newMaxWidthPx : desiredWFromNode;

      baseWidthRef.current = widthPx;
      baseHeightRef.current = desiredH;

      // Position centered on anchor
      // For flowchart shapes and other nodes, use the text node's actual position
      // Recalculate r to ensure we have the latest position
      const currentRect = textNode.getClientRect({ skipStroke: true, skipShadow: true });
      const anchor = centerCanvas
        ? {
            // When a canvas-space center is provided, scale and add stage position
            x: stageBoxNow.left + stagePosNow.x + centerCanvas.x * scaleXNow,
            y: stageBoxNow.top + stagePosNow.y + centerCanvas.y * scaleYNow,
          }
        : {
            // Fallback: currentRect is already in screen space relative to the stage container
            // Use currentRect instead of r to ensure we have the latest position
            x: stageBoxNow.left + (currentRect.x + currentRect.width / 2),
            y: stageBoxNow.top + (currentRect.y + currentRect.height / 2),
          };
      textarea.style.left = `${anchor.x - widthPx / 2}px`;
      textarea.style.top = `${anchor.y - desiredH / 2}px`;
      textarea.style.width = `${widthPx}px`;
      textarea.style.height = `${desiredH}px`;
      anchorLeftRef.current = anchor.x - widthPx / 2;
      anchorRightRef.current = anchor.x + widthPx / 2;

      // Update font size with scale
      const newFontPx = textNode.fontSize() * scaleXNow;
      textarea.style.fontSize = `${newFontPx}px`;
      const ctx2 = measureCanvasRef.current?.getContext('2d');
      if (ctx2) {
        const fontStyle = `${textNode.fontStyle() || ''}`.trim();
        const fontWeight = `${(textNode as any).fontWeight?.() || ''}`.trim();
        const family = textNode.fontFamily() || 'system-ui, -apple-system, sans-serif';
        ctx2.font = `${fontStyle ? fontStyle + ' ' : ''}${fontWeight ? fontWeight + ' ' : ''}${newFontPx}px ${family}`;
      }
      // Match letter-spacing to scaled value to avoid layout drift
      const lsRaw = (textNode as any).letterSpacing?.() || 0;
      (textarea.style as any).letterSpacing = `${lsRaw * scaleXNow}px`;

      // Update centers for subsequent re-centering during input/height changes
      const anchorX = anchor.x;
      const anchorY = anchor.y;
      centerXRef.current = anchorX;
      centerYRef.current = anchorY;

      // Update maxWidth; keep width equal to box width for wrapping editors
      if (typeof newMaxWidthPx === 'number') {
        textarea.style.maxWidth = `${newMaxWidthPx}px`;
        if (!noWrap) {
          textarea.style.width = `${newMaxWidthPx}px`;
          textarea.style.left = `${anchor.x - newMaxWidthPx / 2}px`;
        }
      }

      // Keep rotation in sync
      const rotationNow = textNode.getAbsoluteRotation();
      textarea.style.transform = rotationNow ? `rotateZ(${rotationNow}deg)` : '';
    };

    // Initial position pass + a post-draw correction on next frame
    updatePosition();
    transformRafRef.current = requestAnimationFrame(() => {
      updatePosition();
    });
    // After initial position, adjust height to content and focus
    const updateHeight = () => {
      textarea.style.height = 'auto';
      const contentHeight = textarea.scrollHeight;
      const nextH = Math.max(baseHeightRef.current, contentHeight);
      textarea.style.height = `${nextH}px`;
      
      // When locked to top-left (bounds-based positioning), we don't adjust top position
      // But we may need to recalculate padding-top if content height changed significantly
      if (lockTopLeftRef.current) {
        // Recalculate vertical offset if content height changed
        const vAlign = (textNode.verticalAlign?.() as any) || 'top';
        if (vAlign === 'middle' || vAlign === 'bottom') {
          const heightPx = baseHeightRef.current;
          let newPaddingTop = 0;
          // textarea.scrollHeight includes padding; remove it so we can compute alignment from content height
          const cs = window.getComputedStyle(textarea);
          const padTop = Number.parseFloat(cs.paddingTop || '0') || 0;
          const padBottom = Number.parseFloat(cs.paddingBottom || '0') || 0;
          const contentOnlyHeight = Math.max(0, contentHeight - padTop - padBottom);
          if (vAlign === 'middle') {
            newPaddingTop = Math.max(0, (heightPx - contentOnlyHeight) / 2);
          } else if (vAlign === 'bottom') {
            newPaddingTop = Math.max(0, heightPx - contentOnlyHeight);
          }
          textarea.style.paddingTop = `${newPaddingTop}px`;
        }
      } else {
        // For non-bounds positioning, center the textarea
        const top = centerYRef.current - nextH / 2;
        textarea.style.top = `${top}px`;
      }
    };
    if (noWrap) updateHeight();
    textarea.focus();
    if (selectAllOnMount) {
      if (!useDiv) {
        (textarea as HTMLTextAreaElement).select();
      } else {
        const range = document.createRange();
        range.selectNodeContents(textarea);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    } else if (!useDiv) {
      try {
        const el = textarea as HTMLTextAreaElement;
        const len = el.value.length;
        el.setSelectionRange(len, len);
      } catch {}
    } else {
      const range = document.createRange();
      range.selectNodeContents(textarea);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    // Helpers for committing the current text
    const normalizeNewlines = (value: string) => value.replace(/\r\n/g, '\n');
    const initialTextNormalized = normalizeNewlines(initialText);
    const readCurrentText = () => normalizeNewlines(
      useDiv ? (textarea as HTMLDivElement).innerText : (textarea as HTMLTextAreaElement).value
    );

    const commitAndClose = () => {
      if (isClosing.current) return;
      isClosing.current = true;

      const currentText = readCurrentText();
      let metricsPayload: { width: number; height: number } | undefined;
      let measuredWidthCanvas: number | undefined;
      let measuredHeightCanvas: number | undefined;

      const stageScaleX = stage.scaleX() || 1;
      const stageScaleY = stage.scaleY() || 1;

      // For multi-line text, always use scrollHeight for accurate measurement
      // measureSize() may return single-line height which is incorrect for multi-line text
      // Reset textarea height to auto first to ensure scrollHeight reflects actual content
      // This is important for height decrease - scrollHeight won't shrink if height is fixed
      const originalHeight = textarea.style.height;
      textarea.style.height = 'auto';
      const heightPx = textarea.scrollHeight || parseFloat(textarea.style.height || '0');
      // Restore original height if it was set
      if (originalHeight) {
        textarea.style.height = originalHeight;
      }
      if (!Number.isNaN(heightPx) && heightPx > 0) {
        measuredHeightCanvas = heightPx / stageScaleY;
      }

      // Try measureSize for width if available, but prefer scrollWidth for multi-line
      if (typeof (textNode as any)?.measureSize === 'function') {
        try {
          const measurement = (textNode as any).measureSize(currentText);
          if (measurement) {
            if (typeof measurement.width === 'number' && !Number.isNaN(measurement.width)) {
              measuredWidthCanvas = measurement.width;
            }
            // Don't use measureSize height for multi-line text - it's often incorrect
            // Only use it if we don't have scrollHeight
            if (measuredHeightCanvas === undefined && typeof measurement.height === 'number' && !Number.isNaN(measurement.height)) {
              measuredHeightCanvas = measurement.height;
            }
          }
        } catch {
          // Ignore measurement errors; will fall back to DOM sizing below
        }
      }

      if (measuredWidthCanvas === undefined) {
        const rect = textarea.getBoundingClientRect();
        const widthPx = rect.width || parseFloat(textarea.style.width || '0');
        if (!Number.isNaN(widthPx) && widthPx > 0) {
          measuredWidthCanvas = widthPx / stageScaleX;
        }
      }

      if (measuredWidthCanvas === undefined) {
        try {
          const widthFromNode = typeof textNode.width === 'function' ? textNode.width() : undefined;
          if (typeof widthFromNode === 'number' && !Number.isNaN(widthFromNode)) {
            measuredWidthCanvas = widthFromNode;
          }
        } catch {
          // ignore
        }
      }

      if (measuredHeightCanvas !== undefined) {
        metricsPayload = {
          width: measuredWidthCanvas ?? baseWidthRef.current / stageScaleX,
          height: measuredHeightCanvas
        };
      }

      if (currentText !== initialTextNormalized && (allowEmptyCommit || currentText.trim().length > 0)) {
        onChangeRef.current(currentText, metricsPayload);
      }

      cleanup();
      onCloseRef.current();
    };

    // Event handlers
    const handleBlur = () => {
      commitAndClose();
    };

    const handleOutsideClick = (e: MouseEvent) => {
      if (e.target !== textarea && !isClosing.current) {
        // Trigger blur to commit changes
        textarea.blur();
      }
    };

    const handleKeyDown = (e: Event) => {
      const keyEvent = e as KeyboardEvent;
      e.stopPropagation(); // Prevent canvas shortcuts from firing
      if (keyEvent.key === 'Escape') {
        keyEvent.preventDefault();
        commitAndClose();
      }
    };

    const measureMaxLineWidth = (): number => {
      const text = useDiv ? (textarea as HTMLDivElement).innerText : (textarea as HTMLTextAreaElement).value;
      const lines = text.split('\n');
      let max = 0;
      const ctx2 = measureCanvasRef.current?.getContext('2d');
      if (ctx2) {
        for (const line of lines) {
          const safeLine = line.replace(/\s+/g, ' ');
          const w = ctx2.measureText(safeLine || ' ').width;
          if (w > max) max = w;
        }
      } else {
        max = textarea.scrollWidth;
      }
      return Math.max(baseWidthRef.current, Math.ceil(max) + 2);
    };

    const handleInput = () => {
      // Grow width up to maxWidth while typing when noWrap is enabled
      if (noWrap) {
        const desired = measureMaxLineWidth();
        // Recompute maxWidth in screen pixels using current scale
        const scaleXNow = stage.scaleX();
        const capPx = typeof maxWidth === 'number' ? Math.max(0, maxWidth) * scaleXNow : undefined;
        const capped = typeof capPx === 'number' ? Math.min(desired, capPx) : desired;
        textarea.style.width = `${capped}px`;
        // Re-center horizontally around original midpoint
        if (alignRef.current === 'right') {
          const left = anchorRightRef.current - capped;
          textarea.style.left = `${left}px`;
        } else if (alignRef.current === 'center') {
          const left = centerXRef.current - capped / 2;
          textarea.style.left = `${left}px`;
        } else {
          textarea.style.left = `${anchorLeftRef.current}px`;
        }
        textarea.style.textAlign = alignRef.current as any;
        (textarea.style as any).setProperty?.('text-align-last', alignRef.current);
      }

      // Live-resize for text shapes (includes sticky notes) while typing
      try {
        const parent: any = (textNode as any).getParent?.();
        if (!parent) {
          updateHeight();
          return;
        }
        const isSticky = !!parent.findOne?.('.sticky-rect');
        const bounds: any = parent.findOne?.('.text-bounds');
        if (!bounds) {
          updateHeight();
          return;
        }
        const disableOverlayLiveResize = !!(bounds.getAttr?.('disableOverlayLiveResize') || parent.getAttr?.('disableOverlayLiveResize'));
        const scaleYNow = stage.scaleY();
        const pxHeight = Math.max(baseHeightRef.current, (textarea as HTMLTextAreaElement).scrollHeight || 0);
        const textCanvasHeight = Math.max(18, pxHeight / Math.max(0.0001, scaleYNow));
        if (disableOverlayLiveResize) {
          // Still keep the Konva text + bounds height in sync so nodes can react (e.g., flowchart shapes auto-grow)
          try { (textNode as any).height?.(textCanvasHeight); } catch {}
          try { bounds.height?.(textCanvasHeight); } catch {}
          try { parent.getLayer?.()?.batchDraw?.(); } catch {}
          try { updatePosition(); } catch {}
          updateHeight();
          if (transformRafRef.current) cancelAnimationFrame(transformRafRef.current);
          transformRafRef.current = requestAnimationFrame(() => {
            try { updatePosition(); } catch {}
            updateHeight();
            transformRafRef.current = requestAnimationFrame(() => {
              try { updatePosition(); } catch {}
              updateHeight();
              transformRafRef.current = null;
            });
          });
          return;
        }
        // For sticky notes, keep 10px padding top/bottom in the group + background rect
        const groupCanvasHeight = isSticky ? textCanvasHeight + 20 : textCanvasHeight;
        // Apply to Konva nodes for live preview
        try { (textNode as any).height?.(textCanvasHeight); } catch {}
        try { bounds.height?.(textCanvasHeight); } catch {}
        if (isSticky) {
          const bg: any = parent.findOne?.('.sticky-rect');
          try { bg?.height?.(groupCanvasHeight); } catch {}
          // Re-cache sticky background to reflect new size
          try { bg?.clearCache?.(); bg?.cache?.({ pixelRatio: 1 }); } catch {}
        }
        try { parent.height?.(groupCanvasHeight); } catch {}
        try { parent.getLayer?.()?.batchDraw?.(); } catch {}
      } catch {
        updateHeight();
        return;
      }
      updateHeight();
    };

    // Initial sizing pass to honor longest line for pre-filled values (only for noWrap editors)
    if (noWrap) handleInput();

    // Add listeners to keep overlay in sync with pan/zoom and text node position changes
    const handleStageTransform = () => {
      if (transformRafRef.current) cancelAnimationFrame(transformRafRef.current);
      // Defer until after Konva applies transforms and draws
      transformRafRef.current = requestAnimationFrame(() => {
        updatePosition();
        updateHeight();
        // Some environments need a second tick to stabilize bounding boxes
        transformRafRef.current = requestAnimationFrame(() => {
          updatePosition();
          updateHeight();
          transformRafRef.current = null;
        });
      });
    };
    stage.on('scaleXChange scaleYChange xChange yChange', handleStageTransform);
    
    // Also listen to text node position changes (for when parent group moves)
    const handleTextNodeChange = () => {
      if (transformRafRef.current) cancelAnimationFrame(transformRafRef.current);
      transformRafRef.current = requestAnimationFrame(() => {
        updatePosition();
        updateHeight();
        transformRafRef.current = null;
      });
    };
    // Listen to text node's parent group position changes
    let parentGroup: Konva.Node | null = null;
    try {
      parentGroup = (textNode as any).getParent?.();
      if (parentGroup) {
        parentGroup.on('xChange yChange widthChange heightChange', handleTextNodeChange);
      }
    } catch {}

    const cleanup = () => {
      textarea.removeEventListener('keydown', handleKeyDown);
      textarea.removeEventListener('input', handleInput);
      textarea.removeEventListener('blur', handleBlur);
      window.removeEventListener('click', handleOutsideClick);
      stage.off('scaleXChange scaleYChange xChange yChange', handleStageTransform);
      // Remove text node parent listeners
      if (parentGroup) {
        try {
          parentGroup.off('xChange yChange widthChange heightChange', handleTextNodeChange);
        } catch {}
      }
      if (transformRafRef.current) {
        cancelAnimationFrame(transformRafRef.current);
        transformRafRef.current = null;
      }
      if (document.body.contains(textarea)) {
        document.body.removeChild(textarea);
      }
      textareaRef.current = null;
      isInitialized.current = false;
      isClosing.current = false;
    };

    // Add event listeners
    textarea.addEventListener('keydown', handleKeyDown);
    textarea.addEventListener('input', handleInput);
    textarea.addEventListener('blur', handleBlur);
    
    // Use setTimeout to avoid closing immediately on the same click that opened the editor
    setTimeout(() => {
      window.addEventListener('click', handleOutsideClick);
    }, 100);

    // Cleanup on unmount
    return () => {
      cleanup();
    };
  }, []); // Empty dependency array - only run once on mount

  // This component doesn't render anything itself
  return null;
};
